import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { formatRmb } from '../../lib/marginPreview.js';

const EMPTY_PROFILE = Object.freeze({
  name: '默认电商渠道',
  platform: '',
  platform_commission_pct: 0,
  payment_fee_pct: 0,
  ad_rate_pct: 0,
  return_rate_pct: 0,
  fba_fee_per_unit_rmb: 0,
  overseas_storage_rmb: 0,
  other_channel_cost_rmb: 0,
  is_default: true
});

function pct(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : '—';
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function Kpi({ label, value, hint }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-600">{hint}</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = 'number', suffix }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type={type}
          className="field"
          value={value}
          step={type === 'number' ? '0.01' : undefined}
          min={type === 'number' ? '0' : undefined}
          max={type === 'number' && suffix === '%' ? '100' : undefined}
          onChange={(e) => onChange(type === 'number' ? toNumber(e.target.value) : e.target.value)}
        />
        {suffix && <span className="w-8 text-sm text-slate-700">{suffix}</span>}
      </div>
    </label>
  );
}

export default function CrossBorderChannel() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PROFILE });
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crossborder/summary?limit=100');
      setData(res);
      const first = res?.default_profile || res?.profiles?.[0] || null;
      if (first) {
        setActiveId(first.id);
        setForm({ ...EMPTY_PROFILE, ...first, is_default: true });
      }
    } catch (err) {
      if (err instanceof HttpError && err.status === 403) {
        setError('当前套餐未开通电商/跨境渠道模块。');
      } else {
        setError(err?.message || '加载跨境渠道模块失败');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = data?.items || [];
  const kpis = data?.kpis || {};
  const profiles = data?.profiles || [];
  const profileMap = useMemo(() => new Map(profiles.map((p) => [p.id, p])), [profiles]);

  const selectProfile = (id) => {
    const p = profileMap.get(Number(id));
    if (!p) {
      setActiveId(null);
      setForm({ ...EMPTY_PROFILE });
      return;
    }
    setActiveId(p.id);
    setForm({ ...EMPTY_PROFILE, ...p, is_default: p.is_default });
  };

  const saveProfile = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, is_default: true };
      const saved = activeId
        ? await api.patch(`/api/crossborder/profiles/${activeId}`, payload)
        : await api.post('/api/crossborder/profiles', payload);
      setActiveId(saved.id);
      await load();
    } catch (err) {
      setError(err?.message || '保存渠道配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">电商渠道</h1>
          <p className="mt-1 text-sm text-slate-700">
            接外贸报价后的平台佣金、广告、FBA、退货和海外仓成本，输出渠道净利。
          </p>
        </div>
        <Link to="/app/quotes/new" className="btn-primary">新建带渠道成本的报价</Link>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
        <Kpi label="渠道报价数" value={loading ? '—' : kpis.quotes_with_channel || 0} />
        <Kpi label="渠道销售额" value={formatRmb(kpis.sell_after_discount_rmb)} />
        <Kpi label="渠道成本" value={formatRmb(kpis.channel_cost_rmb)} />
        <Kpi label="渠道净利率" value={pct(kpis.channel_margin_pct)} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[420px_1fr]">
        <section className="rounded-md border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-slate-900">渠道配置</h2>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => { setActiveId(null); setForm({ ...EMPTY_PROFILE }); }}
            >
              新建配置
            </button>
          </div>

          {profiles.length > 0 && (
            <label className="mb-4 block">
              <span className="label">已有配置</span>
              <select
                className="field"
                value={activeId || ''}
                onChange={(e) => selectProfile(e.target.value)}
              >
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.is_default ? ' · 默认' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="配置名称" type="text" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
            <Field label="平台/渠道" type="text" value={form.platform} onChange={(v) => setForm((f) => ({ ...f, platform: v }))} />
            <Field label="平台佣金" suffix="%" value={form.platform_commission_pct} onChange={(v) => setForm((f) => ({ ...f, platform_commission_pct: v }))} />
            <Field label="支付手续费" suffix="%" value={form.payment_fee_pct} onChange={(v) => setForm((f) => ({ ...f, payment_fee_pct: v }))} />
            <Field label="广告 ACOS" suffix="%" value={form.ad_rate_pct} onChange={(v) => setForm((f) => ({ ...f, ad_rate_pct: v }))} />
            <Field label="退货损耗率" suffix="%" value={form.return_rate_pct} onChange={(v) => setForm((f) => ({ ...f, return_rate_pct: v }))} />
            <Field label="FBA/配送费" value={form.fba_fee_per_unit_rmb} onChange={(v) => setForm((f) => ({ ...f, fba_fee_per_unit_rmb: v }))} />
            <Field label="海外仓储费" value={form.overseas_storage_rmb} onChange={(v) => setForm((f) => ({ ...f, overseas_storage_rmb: v }))} />
            <div className="sm:col-span-2">
              <Field label="其他渠道成本" value={form.other_channel_cost_rmb} onChange={(v) => setForm((f) => ({ ...f, other_channel_cost_rmb: v }))} />
            </div>
          </div>

          <button
            type="button"
            className="btn-primary mt-4 w-full"
            disabled={saving}
            onClick={saveProfile}
          >
            {saving ? '保存中…' : '保存为默认渠道配置'}
          </button>
          <p className="mt-3 text-xs leading-5 text-slate-600">
            配置用于复用渠道参数；每张报价保存后，实际渠道净利仍以报价当时的快照为准。
          </p>
        </section>

        <section className="rounded-md border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="text-base font-semibold text-slate-900">渠道报价快照</h2>
            <span className="text-sm text-slate-600">最近 {rows.length} 条</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium text-slate-700">
                <tr>
                  <th className="px-4 py-3">报价</th>
                  <th className="px-4 py-3">客户</th>
                  <th className="px-4 py-3 text-right">折后销售</th>
                  <th className="px-4 py-3 text-right">渠道成本</th>
                  <th className="px-4 py-3 text-right">渠道净利</th>
                  <th className="px-4 py-3 text-right">净利率</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-600">加载中…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-600">
                      暂无带 C 层参数的报价。进入 <Link className="text-chango-700 underline" to="/app/quotes/new">新建报价</Link> 后填写渠道成本即可沉淀快照。
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/app/quotes/${row.id}`} className="font-medium text-chango-700 hover:underline">
                        {row.code}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-800">{row.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.sell_after_discount_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.channel_cost_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{formatRmb(row.channel_net_profit_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{pct(row.channel_margin_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
